"use strict";

const UP = 'up';
const DOWN = 'down';

const ICON_ARROW = 'arrow';
const ICON_MIN_PLUS = 'min-plus';

/**
 * Class AutoCollapse
 *
 * Html like:
 *
 *      <div data-auto-collapse="tags" id="seo">
 *          <h1>header title</h1>
 *          <p>text for header title</p>
 *          <h2>title 1</h2>
 *          <p>text for title 1</p>
 *          <table><tbody><tr><td>table for title 1</tr></tbody></table>
 *          <blockquote>block quote for title 1</blockquote>
 *          <h4>title 2</h4>
 *          <video>video for title 2</video>
 *          <a>link for title 2</a>
 *          <p>text for title 2</p>
 *          <ul><li>list for title 2</li></ul>
 *      </div>
 *
 * And Javascript like:
 *
 *      require('bootstrap/js/dist/collapse');
 *      import AutoCollapse from '/path/to/auto-collapse';
 *      new AutoCollapse({
 *          'container': '#seo',
 *      });
 *
 * Is automatically converted to bootstrap collapse html:
 *
 *      <div id="seo" class="auto-collapse auto-collapse--accordion">
 *          <h1 class="auto-collapse__nch auto-collapse__nch-0">header title</h1>
 *          <p>text for header title</p>
 *          <div class="auto-collapse__item auto-collapse__item-0 auto-collapse__item-first" id="seo-collapse-0">
 *              <button class="auto-collapse__button collapsed"
 *                      type="button"
 *                      aria-expanded="true"
 *                      aria-controls="seocollapse0"
 *                      data-toggle="collapse"
 *                      data-target="#seocollapse0"
 *              >
 *                  <h2>title 1</h2>
 *                  <i class="fas fa-chevron-down"></i>
 *              </button>
 *              <div id="seocollapse0"
 *                   class="auto-collapse__content collapse show"
 *                   data-parent="#seo"
 *              >
 *                  <p>text for title 1</p>
 *                  <table><tbody><tr><td>table for title 1</tr></tbody></table>
 *                  <blockquote>block quote for title 1</blockquote>
 *              </div>
 *          </div>
 *          <div class="auto-collapse__item auto-collapse__item-1 auto-collapse__item-last" id="seo-collapse-1">
 *              <button class="auto-collapse__button collapsed"
 *                      type="button"
 *                      aria-expanded="false"
 *                      aria-controls="seocollapse1"
 *                      data-toggle="collapse"
 *                      data-target="#seocollapse1"
 *              >
 *                  <h4>title 2</h4>
 *                  <i class="fas fa-chevron-down"></i>
 *              </button>
 *              <div id="seocollapse1"
 *                   class="auto-collapse__content collapse"
 *                   data-parent="#seo"
 *              >
 *                  <video>video for title 2</video>
 *                  <a>link for title 2</a>
 *                  <p>text for title 2</p>
 *                  <ul><li>list for title 2</li></ul>
 *              </div>
 *          </div>
 *      </div>
 *
 * This html allows bootstrap collapse to work. See: https://getbootstrap.com/docs/4.0/components/collapse/
 * This code is only compatible with bootstrap 4
 */
export default class AutoCollapseTags {

    /**
     * Constructor
     *
     * @param {string|Element} container         Container path or node where the auto-collapse functionality has to operate. Example: '.path .to #container'
     *                                           Default container path is "data-auto-collapse='tags'"
     * @param {boolean} [expandFirst]            expand first collapsible item at initiation.
     * @param {boolean} [accordion]              allow collapsible items to function as accordion. This means when one collapsible item is expanded, the others are collapsed
     * @param {array} [collapsibleHeaderTags]    define which tags should operate as collapsible buttons
     * @param {array} [nonCollapsibleHeaderTags] define which tags should "not" operate as collapsible buttons and should not be included in the collapsible content
     * @param {int} [fontAwesomeVersion]         font Awesome Version currently installed in project. Accepted values: 4 and 5.
     * @param {string} [breakpointUp]            only activate auto-collapse when window width is "larger than" bootstrap breakpoint value. Accepted bootstrap breakpoints are: 'xs', 'sm', 'md', 'lg' and 'xl'. This feature is disabled when value is undefined.
     * @param {string} [breakpointDown]          only activate auto-collapse when window width is "smaller than" bootstrap breakpoint value. Accepted bootstrap breakpoints are: 'xs', 'sm', 'md', 'lg' and 'xl'. This feature is disabled when value is undefined.
     * @param {string} iconType                  Type of icon in collapsible button. Options: 'arrow' or 'min-plus'
     * @param {boolean} addLine                  Add a line between collapsible headers.
     */
    constructor({
        container,
        expandFirst = false,
        accordion = true,
        collapsibleHeaderTags = [
            'h2',
            'h3',
            'h4',
            'h5',
        ],
        nonCollapsibleHeaderTags = [
            'h1',
            'hr',
        ],
        fontAwesomeVersion = 5,
        breakpointUp = undefined,
        breakpointDown = undefined,
        iconType = ICON_MIN_PLUS,
        addLine = false,
    }) {
        this.config = {
            container,
            expandFirst,
            accordion,
            collapsibleHeaderTags,
            nonCollapsibleHeaderTags,
            fontAwesomeVersion,
            breakpointUp,
            breakpointDown,
            iconType,
            addLine,
        }

        // Bootstrap Breakpoints
        this.bootstrapBreakpoints = {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
        };

        // classes according to BEM naming guide: http://getbem.com/naming/
        this.classes = {
            base: 'auto-collapse',
            modifier: {
                accordion: 'auto-collapse--accordion',
            },
            element: {
                item: 'auto-collapse__item',
                button: 'auto-collapse__button',
                content: 'auto-collapse__content',
                nonCollapsibleHeader: 'auto-collapse__nch'
            },
        }

        this.buildAutoCollapse();
    }

    /**
     * Build Automatic Collapse
     */
    buildAutoCollapse() {
        if (undefined !== this.config.breakpointUp && false === this.renderAtBreakpoint(this.config.breakpointUp, UP)) return;
        if (undefined !== this.config.breakpointDown && false === this.renderAtBreakpoint(this.config.breakpointDown, DOWN)) return;

        this.collapsibleHeaders = [].concat(this.config.collapsibleHeaderTags);
        this.nonCollapsibleHeaders = [].concat(this.config.nonCollapsibleHeaderTags);
        const containers = 'object' === typeof this.config.container ? [this.config.container] : document.querySelectorAll(this.config.container ?? '[data-auto-collapse="tags"]');
        containers.forEach((container, index) => {
            const globalIdName = '' !== container.id ? container.id : container.className.replace(/[&\s\/\\#\[\],=+\-()$~%.'":*?<>{}]/g, '');

            // prevent rebuild of autoCollapse if autoCollapse already exists
            if (0 < container.getElementsByClassName(this.classes.element.button).length) {
                return;
            }

            // wrapper
            container.classList.add(this.classes.base);
            if (true === this.config.accordion) container.classList.add(this.classes.modifier.accordion);

            this.idName = (() => {
                if ('' === container.id) {
                    container.id =  globalIdName + index;
                    return globalIdName + index;
                } else {
                    return container.id;
                }
            }) ();

            // collapsible tags
            const headerNodesCollapsible = container.querySelectorAll(this.collapsibleHeaders.join());
            const headerNodesCollapsibleLength = headerNodesCollapsible.length;
            headerNodesCollapsible.forEach((headerNode, index) => {
                this.localId = this.idName + 'collapse' + index;
                this.expandFirst = (0 === index && true === this.config.expandFirst);

                let bootstrapItem = this.createItem(headerNodesCollapsibleLength, index);
                bootstrapItem.appendChild(this.createButton(headerNode));
                bootstrapItem.appendChild(this.createContent(headerNode));
                headerNode.parentNode.insertBefore(bootstrapItem, headerNode.nextSibling);
                headerNode.remove();
            })

            // non-collapsible tags
            const headerNodesNonCollapsible = container.querySelectorAll(this.nonCollapsibleHeaders.join());
            headerNodesNonCollapsible.forEach((headerNode, index) => {
               this.modifyNonCollapsibleHeaders(headerNode, index);
            });
        })
    }

    /**
     * Create Collapsible Item
     *
     * @param {int} nodeLength total Items
     * @param {int} index      x item in array
     *
     * @returns {HTMLDivElement}
     */
    createItem(nodeLength, index) {
        let bootstrapItem = document.createElement('div');
        bootstrapItem.classList.add(this.classes.element.item, this.classes.element.item + '-' + index);
        // Add the ID attribute to each item here
        bootstrapItem.id = this.config.container.id + '-collapse-' + index;
        if (0 === index) bootstrapItem.classList.add(this.classes.element.item + '-first');
        if (nodeLength === (index + 1)) bootstrapItem.classList.add(this.classes.element.item + '-last');

        return bootstrapItem;
    }

    /**
     * Modify Non Collapsible Tag
     *
     * @param {object} node  total Items
     * @param {int}    index x item in array
     */
    modifyNonCollapsibleHeaders(node, index) {
        node.classList.add(this.classes.element.nonCollapsibleHeader, this.classes.element.nonCollapsibleHeader + '-' + index);
    }

    /**
     * Create Collapsible Button
     *
     * @param {object} node     node to convert to button
     *
     * @returns {HTMLButtonElement}
     */
    createButton(node) {
        let bootstrapButton = document.createElement('button');
        bootstrapButton.classList.add(this.classes.element.button);
        if (true === this.config.addLine) bootstrapButton.classList.add(this.classes.element.button + '--line');

        let nodeClone = node.cloneNode(false);
        nodeClone.innerHTML = node.innerText;
        bootstrapButton.appendChild(nodeClone);

        bootstrapButton.setAttribute('type', 'button');
        bootstrapButton.setAttribute('aria-expanded', true === this.expandFirst ? 'true' : 'false');
        bootstrapButton.setAttribute('aria-controls', this.localId);
        bootstrapButton.setAttribute('data-toggle', 'collapse');
        bootstrapButton.setAttribute('data-target', '#' + this.localId);

        if (this.config.iconType === ICON_ARROW) {
            bootstrapButton.appendChild(this.createArrowIcon());
        }

        if (this.config.iconType === ICON_MIN_PLUS) {
            bootstrapButton.appendChild(this.createMinPlusIcon());
        }

        return bootstrapButton;
    }

    /**
     * Create Fontawesome Chevron Icon
     *
     * @returns {HTMLElement}
     */
    createArrowIcon() {
        let chevronIcon = document.createElement('i');
        switch(this.config.fontAwesomeVersion) {
            case 4:
                chevronIcon.classList.add('fa', 'fa-chevron-down');
                break;
            case 5:
                chevronIcon.classList.add('fas', 'fa-chevron-down');
                break;
        }

        return chevronIcon;
    }

    /**
     * Create plus min icon
     *
     * @returns {HTMLSpanElement}
     */
    createMinPlusIcon() {
        let minPlusIconWrapper = document.createElement('span');
        minPlusIconWrapper.classList.add(this.classes.element.button + '__plus-min-icon');

        let minIcon = document.createElement('span');
        minIcon.classList.add('min');
        minIcon.innerText = '-';

        let plusIcon = document.createElement('span');
        plusIcon.classList.add('plus');
        plusIcon.innerText = '+';

        minPlusIconWrapper.appendChild(minIcon);
        minPlusIconWrapper.appendChild(plusIcon);

        return minPlusIconWrapper;
    }

    /**
     * Create Collapsible Content Element
     *
     * @param {object} node     node where the siblings of this node creates the content
     *
     * @returns {HTMLDivElement}
     */
    createContent(node) {
        let bootstrapContent = document.createElement('div');
        bootstrapContent.id = this.localId;
        bootstrapContent.classList.add(this.classes.element.content, 'collapse');
        if (true === this.expandFirst) bootstrapContent.classList.add('show');
        if (true === this.config.accordion) bootstrapContent.setAttribute('data-parent', '#' + this.idName);

        let nextNode = node;
        let nextNodes = [];
        while (
            null !== nextNode &&
            (nextNode = nextNode.nextElementSibling) &&
            false === this.collapsibleHeaders.includes(nextNode.tagName.toLowerCase()) &&
            false === this.nonCollapsibleHeaders.includes(nextNode.tagName.toLowerCase())
            ) {
            nextNodes.push(nextNode);
        }

        nextNodes.forEach(nextNodeEl => {
            bootstrapContent.appendChild(nextNodeEl);
        })

        return bootstrapContent;
    }

    /**
     * Decide if auto-collapse should work based on breakpoint
     *
     * @param {string} breakpoint   Breakpoint value. Accepted values: 'xs', 'sm', 'md', 'lg' and 'xl'
     * @param {string} upOrDown     Breakpoint Up or Down: Accepted values: 'up' and 'down'
     *
     * @returns {boolean}
     */
    renderAtBreakpoint(breakpoint, upOrDown) {
        const windowWidth = window.innerWidth ?? document.documentElement.clientWidth ?? document.body.clientWidth;

        if ('undefined' === this.bootstrapBreakpoints[breakpoint]) {
            console.error('unknown key ' + breakpoint + ' in array this.bootstrapBreakpoints');

            return false;
        }

        return (
            (DOWN === upOrDown && this.bootstrapBreakpoints[breakpoint] > windowWidth) ||
            (UP === upOrDown && this.bootstrapBreakpoints[breakpoint] < windowWidth)
        );
    }
}
